import { useEffect, useState, useRef, useCallback } from 'react';
import { grey } from '@mui/material/colors';
import { Settings } from '@mui/icons-material';
import { Grid, Container, IconButton, SwipeableDrawer, Box, Toolbar, Drawer, useTheme, Typography, Backdrop } from '@mui/material';
import { isMobile, useMobileOrientation } from 'react-device-detect';
import { useDispatch, useSelector } from "react-redux"
import { setSource, getData, setIndex, loadNextImage, loadPrevImage, setTimestamp, setEvents, setInitialFooterHeight } from "../../api/dataSlice"
import { DateTimePicker, LocalizationProvider, TimePicker, CalendarPickerSkeleton } from '@mui/lab';
import { checkFile, normalizeWeatherCode, useWindowDimensions } from "../Utilities"
import { useGetDataByCameraQuery, useLazyGetTagsByCameraQuery } from "../../api/graphqlApi";
import { useTranslation } from 'react-i18next';
import { Accordion, AccordionSummary, AccordionDetails } from '../Accordion';
import Download from './Download'
import Calendar from './Calendar'
import Events from './Events'
import TimeSelector from './TimeSelector'
import DayjsAdapter from '@mui/lab/AdapterDayjs';
import i18next from 'i18next';
import Diary from './Diary';
import dayjs from 'dayjs';
import Report from './Report';

export default function ArchiveControls({ initialSiderWidth, onOpenSideDrawer, setPaddingFooter, drawerBleeding, component }) {

    const { CAMERAS, WEATHER_CATEGORY, DEBUG, LAYOUT: { DIARY, TITLE }, BASEURL } = window.conf
    const { t } = useTranslation()
    const { source, index, timestamp, events } = useSelector(getData);
    const { isPortrait, isLandscape } = useMobileOrientation()
    const { data, isLoading: loading } = useGetDataByCameraQuery(null, { pollingInterval: 600000 })
    const { contentHeight, appbarHeight } = useWindowDimensions()

    const [getTagsByCamera] = useLazyGetTagsByCameraQuery()
    const [canStepLeft, setCanStepLeft] = useState(false)
    const [canStepRight, setCanStepRight] = useState(false)
    const [footerOpen, setFooterOpen] = useState(false)
    const [footerHeight, setFooterHeight] = useState(48)
    const [firstRun, setFirstRun] = useState(true)
    const [pickerOpen, setPickerOpen] = useState(false)
    const [loadingEvents, setLoadingEvents] = useState(false)
    const [isDrawerOpen, setIsDrawerOpen] = useState(false)
    const [openTagModal, setOpenTagModal] = useState(false)
    const [tagData, setTagData] = useState()
    const [expanded, setExpanded] = useState(false)
    const [siderWidth, setSiderWidth] = useState()

    const refSider = useRef()
    const dispatch = useDispatch()
    const accordionOffset = 32; // 16px padding on each side
    const enableDiary = DIARY.ENABLED
    const minDate = dayjs(source?.[0]?.[0]?.[0])
    const maxDate = dayjs(source?.[0]?.[source[0].length - 1]?.[0])
    const startYRef = useRef(0);
    const isPullingRef = useRef(false);
    const theme = useTheme();
    const closedFooterHeight = footerOpen
        ? events?.length
            ? footerHeight + 162 + events.length * 33 > contentHeight // higher than 80% of window height
                ? contentHeight - 20 // limit to content height
                : footerHeight + 162 + (events.length * 33) // depending on footer elements and data entrys
            : footerHeight + 189 // depending on footer elements (picker, divider, searchbar, no-data display)
        : footerHeight
    const fullHeight = isPortrait ? "100vh" : `calc(100vh - ${appbarHeight}px)`
    const isArchive = component === 3
    const isMySite = component === 4

    // report

    const [report, setReport] = useState({
        selectedCameraId: null,
        checkedCameras: [],
        loading: true
    });

    const setReportCameraId = (id) => setReport(prevState => ({ ...prevState, selectedCameraId: id }))

    const moreThanOneCam = CAMERAS.length > 1
    const selectedReport = report.checkedCameras[report.selectedCameraId] || {}
    const availableCameras = Object.keys(report.checkedCameras).filter(id => report.checkedCameras[id].exists).map(Number)
    const title = TITLE ? `${TITLE}_` : ""
    const camera = selectedReport?.name
        ? `${selectedReport.name}_`
        : moreThanOneCam
            ? `${t("events.camera")} ${selectedReport.id + 1}_`
            : ""
    const reportTargetFilename = `${title}${camera}${t("events.report")}_${dayjs(selectedReport.lastModified).format("YY-MM-DD")}`
    const reportFilePath = selectedReport?.filePath

    useEffect(() => {
        const checkAllReports = async () => {
            if (isMySite) {
                setReport(prev => ({ ...prev, loading: true }))
    
                const results = await Promise.all(
                    CAMERAS.map(async (elem) => {
                        const path = `${BASEURL}cam${elem.id + 1}/reports/project_report.pdf`
                        const { exists, contentType, lastModified } = await checkFile(path)
                        return {
                            id: elem.id,
                            name: elem.name,
                            exists: exists && contentType?.includes('pdf'),
                            lastModified,
                            filePath: path
                        }
                    })
                )
    
                const availableCameras = results.filter(elem => elem.exists)
                const firstAvailable = availableCameras[0]?.id ?? null
    
                setReport({
                    selectedCameraId: firstAvailable,
                    checkedCameras: results,
                    loading: false
                })
            }
        }

        checkAllReports()
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!report.loading) {
            if (selectedReport?.exists) {
                setExpanded('report')
            } else if (enableDiary) {
                setExpanded('diary')
            } else {
                setExpanded('archive')
            }
        }
    }, [report]) // eslint-disable-line react-hooks/exhaustive-deps

    const onChangeExpanded = (panel) => (event, newExpanded) => {
        setExpanded(newExpanded ? panel : false);
    }

    const onOpenTagModal = (row) => {
        setOpenTagModal(true)
        setTagData(row)
    }

    const onCloseTagModal = () => {
        setOpenTagModal(false)
        setTagData(null)
    }

    const toggleSideDrawer = (open) => {
        setIsDrawerOpen(open);
        onOpenSideDrawer(open);

        if (open) {
            document.body.style.overflow = 'auto'; // keep background (content) scrollable
        }
    }

    const toggleBottomDrawer = (component) => {
        if (component) {
            setFooterHeight(20)
            setExpanded(component)
            setFooterOpen(true)
        } else {
            setTimeout(() => {
                setFooterOpen(false);
            }, theme.transitions.duration.leavingScreen);
        }
    };

    const updateTimestamp = (timestamp) => {
        dispatch(setTimestamp(timestamp));
    }

    // read tags from database - necessary to get consistent event list after CRUD operations by AddTag
    const readTags = useCallback(async () => {
        if (source && index) {
            try {
                var archivePictureByCamera = source[0]?.[index]?.[1]
                var archivePicturesDetailsByCamera = await getTagsByCamera({ archivePictureByCamera: archivePictureByCamera })
                var tags =
                    archivePicturesDetailsByCamera?.data?.map((details, index) =>
                        details?.map(elem => {
                            let isWeather = elem.kategorie === WEATHER_CATEGORY
                            let category = isWeather ? t(`weather.weather`) : elem.kategorie
                            let description = isWeather ? t(`weather.conditions.${normalizeWeatherCode(elem.beschreibung)}`) : elem.beschreibung
                            let weatherArchives = elem.weatherArchives?.filter(elem => elem.archiveType === 0)?.[0] // meteomatics
                            let weatherArchivesDWD = elem.weatherArchives?.filter(elem => elem.archiveType === 2)?.[0] // DWD

                            if (isWeather && weatherArchives) {
                                description =
                                    `${Math.round(weatherArchives.temp)}°, ` +
                                    `${t(`weather.conditions.${normalizeWeatherCode(weatherArchives.weatherCode)}`)}, ` +
                                    `${t(`weather.wind`)} ${weatherArchives.windSpeed} km/h, ` +
                                    `${t(`weather.humidity`)} ${weatherArchives.humidity}%`

                                if (weatherArchivesDWD) {
                                    description +=
                                        ` (DWD: ${Math.round(weatherArchivesDWD.temp)}°, ` +
                                        `${t(`weather.wind`)} ${weatherArchivesDWD.windSpeed} km/h, ` +
                                        `${t(`weather.humidity`)} ${weatherArchivesDWD.humidity}%)`
                                }
                            }

                            return ({
                                id: elem.id,
                                camera: CAMERAS[index],
                                category: category,
                                description: description,
                                archivePicturesId: elem.archivePicturesId
                            })
                        })
                    ).flat().filter(elem => elem) // filter empty tags

                return tags ?? []

            } catch (error) {
                console.error("[ArchiveControls] error", error)
            }
        }
    }, [index, source, getTagsByCamera]) // eslint-disable-line react-hooks/exhaustive-deps

    const loadEvents = useCallback(async () => {

        setLoadingEvents(true)

        var tags = await readTags()

        if (tags) {

            var events = [...tags]
            var eventsHeight = (footerOpen ? 20 : 48 + (events.length <= 3 ? events.length : 3) * 29)

            if (DEBUG) {
                console.log("[ArchiveControls] events", events)
            }

            dispatch(setEvents(events))
            dispatch(setInitialFooterHeight(eventsHeight))
            setFooterHeight(eventsHeight)
        }

        setLoadingEvents(false)
    }, [readTags, dispatch, footerOpen]) // eslint-disable-line react-hooks/exhaustive-deps

    const handleTouchStart = (e) => {
        startYRef.current = e.touches[0].clientY;
        isPullingRef.current = e.target.closest(".puller") !== null;
    }

    const handleTouchMove = (e) => {
        const deltaY = e.touches[0].clientY - startYRef.current;

        if (isPullingRef.current && deltaY > 30) {
            toggleBottomDrawer(false)
        }
    }

    function stepLeft() {
        if (Number.isFinite(index) && canStepLeft) {
            dispatch(loadPrevImage());
        }
    }

    function stepRight() {
        if (Number.isFinite(index) && canStepRight) {
            dispatch(loadNextImage());
        }
    }

    function shouldDisableDate(date) {
        var dict = source?.[1]
        var hasDay = dict?.[date.$y]?.[date.$M]?.[date.$D]
        return hasDay ? false : true
    }

    function shouldDisableTime(time, type) {
        var dict = source?.[1]
        switch (type) {
            case "hours":
                var hasHour = dict?.[timestamp?.$y]?.[timestamp?.$M]?.[timestamp?.$D]?.[time]
                return hasHour ? false : true
            case "minutes":
                var hasMinute = dict?.[timestamp?.$y]?.[timestamp?.$M]?.[timestamp?.$D]?.[timestamp?.$H]?.[time]
                return hasMinute ? false : true
            default:
                return false
        }
    }

    useEffect(() => {
        setPaddingFooter(closedFooterHeight)
    }, [closedFooterHeight, setPaddingFooter])

    useEffect(() => {
        if (isMySite) {
            loadEvents()
        } else {
            setFooterHeight(48)
            dispatch(setInitialFooterHeight(48))
        }
    }, [isMySite, loadEvents, dispatch])

    useEffect(() => {
        if (index) {
            updateTimestamp(source?.[0]?.[index]?.[0])
        }
    }, [index]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (data) {
            if (firstRun) {
                var list = data[0]
                var lastIndex = list.length - 1
                var lastElement = list[lastIndex]

                // set time in TimeSelector for display clock by date of last image (ONLY ONCE!)
                updateTimestamp(lastElement?.[0])

                // set index in redux store for Controls
                dispatch(setIndex(lastIndex))

                setFirstRun(false)
            }

            // set source in redux store for Content
            dispatch(setSource(data))

            if (DEBUG) {
                console.log("[ArchiveControls] source", data)
            }
        }
    }, [data, dispatch]) // eslint-disable-line react-hooks/exhaustive-deps

    // enable/disable stepping left/right executed by TimeSelector
    useEffect(() => {
        if (source && Number.isFinite(index)) {
            var list = source[0]
            var incrementedIndex = index - 1
            var decrementedIndex = index + 1

            if (list?.[incrementedIndex]) {
                setCanStepLeft(true)
            } else {
                setCanStepLeft(false)
            }

            if (list?.[decrementedIndex]) {
                setCanStepRight(true)
            } else {
                setCanStepRight(false)
            }
        }
    }, [source, index])

    useEffect(() => {
        if (footerOpen) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "auto";
        }
        return () => document.body.style.overflow = "auto";
    }, [footerOpen])

    useEffect(() => {
        return () => {
            onOpenSideDrawer(false)
            setIsDrawerOpen(false)
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps  

    useEffect(() => {
        const current = refSider.current;
        if (!current) return;

        const observer = new ResizeObserver(() => {
            const newWidth = current.clientWidth - accordionOffset; // current sider width without padding of accordion
            setSiderWidth((prevWidth) => (prevWidth !== newWidth ? newWidth : prevWidth)); // only update if the new width is different from the current state
        });

        observer.observe(current);
        return () => observer.disconnect();
    }, []);

    const controlsArchive = (

        // archive has no accordion so elements need margin/padding
        <Box sx={{
            ...(isArchive && { mt: 2, pr: 2 }),
            ...(isArchive && !isMobile && { pl: 2 })
        }}> 

            {/* timepicker only for landscape */}
            {isLandscape && ( 
                <Box sx={{ justifyContent: "center", display: "flex" }}>
                    <LocalizationProvider
                        dateAdapter={DayjsAdapter}
                        locale={i18next.language}
                    >
                        <TimePicker
                            open={pickerOpen}
                            onClose={() => setPickerOpen(false)}
                            value={timestamp}
                            onChange={updateTimestamp}
                            renderInput={({ inputRef }) =>
                                <TimeSelector
                                    stepLeft={stepLeft}
                                    timestamp={timestamp}
                                    stepRight={stepRight}
                                    loading={loading}
                                    canStepLeft={canStepLeft}
                                    canStepRight={canStepRight}
                                    setPickerOpen={setPickerOpen}
                                    inputRef={inputRef}
                                    pb={isLandscape ? 2 : 0}
                                />
                            }
                            cancelText={t("actions.cancel")}
                            shouldDisableTime={shouldDisableTime}
                            showToolbar={false}
                        />
                    </LocalizationProvider>
                </Box>
            )}

            <Calendar
                timestamp={timestamp}
                setTimestamp={updateTimestamp}
                siderWidth={siderWidth}
                shouldDisableDate={shouldDisableDate}
                loading={loading}
                loadingEvents={loadingEvents}
                openTagModal={openTagModal}
                onOpenTagModal={onOpenTagModal}
                onCloseTagModal={onCloseTagModal}
                tagData={tagData}
                extended={isMySite}
            />

        </Box>
    )

    const controlsMySite = (
        <Box sx={{ width: "100%" }}>

            {/* report */}

            {availableCameras.length > 0 && (
                <Accordion
                    expanded={expanded === 'report'}
                    onChange={onChangeExpanded('report')}
                    isPortrait={isPortrait}
                >
                    <AccordionSummary className="accordion-with-subtitle">
                        <Box>
                            <Typography>{t("events.report")}</Typography>
                            {selectedReport.lastModified && (
                                <Typography className="accordion-subtitle">
                                    {`${t("events.creation_date")}: ${dayjs(selectedReport.lastModified).format(t("date.format.shortest"))}`}
                                </Typography>
                            )}
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                        <Report
                            loading={report.loading}
                            fileExists={selectedReport.exists || false}
                            alertMessage={!selectedReport.exists ? { type: 'error', message: t('msg.no_file') } : null}
                            filePath={reportFilePath}
                            targetFilename={reportTargetFilename}
                            cameraId={report.selectedCameraId}
                            setCameraId={setReportCameraId}
                            availableCameras={availableCameras}
                        />
                    </AccordionDetails>

                </Accordion>
            )}

            {/* diary */}

            {enableDiary && (
                <Accordion
                    expanded={expanded === 'diary'}
                    onChange={onChangeExpanded('diary')}
                    isPortrait={isPortrait}
                >
                    <AccordionSummary className="accordion-with-subtitle">
                        <Box>
                            <Typography>{t("events.construction_diary.construction_diary")}</Typography>
                            <Typography className="accordion-subtitle">{`${t("events.construction_diary.analysis")} & ${t("weather.weather")}`}</Typography>
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                        <Diary
                            loading={loading}
                            timestamp={timestamp}
                            setTimestamp={updateTimestamp}
                            shouldDisableDate={shouldDisableDate}
                            siderWidth={siderWidth}
                            footerOpen={footerOpen}
                        />
                    </AccordionDetails>

                </Accordion>
            )}

            {/* download - for mobiles generally disabled */}

            {!isMobile && (
                <Accordion
                    expanded={expanded === 'download'}
                    onChange={onChangeExpanded('download')}
                    isPortrait={isPortrait}
                >
                    <AccordionSummary className="accordion-with-subtitle">
                        <Box>
                            <Typography>{t("actions.download")}</Typography>
                            <Typography className="accordion-subtitle">{`${t("pages.timelapse")} & ${t("download.images")}`}</Typography>
                        </Box>
                    </AccordionSummary>

                    <AccordionDetails>
                        <Download
                            timestamp={timestamp}
                            siderWidth={siderWidth}
                            shouldDisableDate={shouldDisableDate}
                            loading={loading}
                        />
                    </AccordionDetails>

                </Accordion>
            )}

            {/* archive */}

            <Accordion
                expanded={expanded === 'archive'}
                onChange={onChangeExpanded('archive')}
                isPortrait={isPortrait}
            >
                <AccordionSummary>
                    {t("events.events")}
                </AccordionSummary>

                <AccordionDetails>
                    {controlsArchive}
                </AccordionDetails>

            </Accordion>

        </Box>
    )

    // mobile footer portrait
    if (isMobile && isPortrait) {
        return (
            <Container sx={{ backgroundColor: "black", height: "100%" }}>

                {/* grays out background when footer open */}
                
                <Backdrop
                    open={footerOpen}
                    onClick={(e) => {
                        e.stopPropagation()
                        e.preventDefault()
                        toggleBottomDrawer(false)
                    }}
                />

                <SwipeableDrawer
                    anchor="bottom"
                    open={footerOpen}
                    onClose={() => toggleBottomDrawer(false)}
                    onOpen={() => toggleBottomDrawer("archive")}
                    swipeAreaWidth={drawerBleeding + 9} // increases the area from which swiping is possible
                    disableSwipeToOpen={true} // prevent opening by swiping because its opened by buttons
                    ModalProps={{
                        keepMounted: true,
                        closeAfterTransition: true,
                        disableRestoreFocus: true,
                        hideBackdrop: true
                    }}
                    PaperProps={{
                        sx: {
                            height: `calc(${footerHeight} - ${drawerBleeding}px)`,
                            overflow: 'visible'
                        },
                        onTouchStart: footerOpen ? handleTouchStart : undefined,
                        onTouchMove: footerOpen ? handleTouchMove : undefined
                    }}
                    variant={footerOpen ? "persistent" : "temporary"}
                >

                    <Box
                        sx={{
                            position: 'absolute',
                            top: -footerHeight,
                            borderTopLeftRadius: 8,
                            borderTopRightRadius: 8,
                            visibility: 'visible',
                            right: 0,
                            left: 0,
                            backgroundColor: "white"
                        }}
                    >

                        {/* puller component to pull out the drawer */}

                        {footerOpen ? (
                            <Box
                                className="puller"
                                sx={{
                                    width: "100%",
                                    height: 24,
                                    display: "flex",
                                    justifyContent: "center",
                                    alignItems: "center"
                                }}
                                onClick={(e) => e.stopPropagation()}
                            >
                                <Box
                                    sx={{
                                        width: 30,
                                        height: 6,
                                        backgroundColor: grey[300],
                                        borderRadius: 3
                                    }}
                                />
                            </Box>
                        ) : (
                            <Grid
                                container
                                columns={{ xs: 1 }}
                                sx={{ height: footerOpen ? "inherit" : "unset" }}
                            >
                                <Grid
                                    item
                                    xs={1}
                                    sx={{ height: footerOpen ? "inherit" : "unset" }}
                                >
                                    <Box sx={{
                                        position: "relative",
                                        width: "100%",
                                        display: "flex",
                                        justifyContent: "center"
                                    }}>

                                        <LocalizationProvider
                                            dateAdapter={DayjsAdapter}
                                            locale={i18next.language}
                                        >
                                            <DateTimePicker
                                                ampm={false}
                                                label={t("date.date_n_time")}
                                                inputFormat={(t("date.format.full_date_time"))}
                                                mask={t("date.mask.full_date_time")}
                                                renderInput={({ inputRef }) =>
                                                    <TimeSelector
                                                        stepLeft={stepLeft}
                                                        timestamp={timestamp}
                                                        stepRight={stepRight}
                                                        showDate={true}
                                                        canStepLeft={canStepLeft}
                                                        canStepRight={canStepRight}
                                                        loading={loading}
                                                        setPickerOpen={setPickerOpen}
                                                        inputRef={inputRef}
                                                    />
                                                }
                                                value={timestamp}
                                                onChange={updateTimestamp}
                                                cancelText={t("actions.cancel")}
                                                toolbarTitle={t("date.select_datetime")}
                                                minDate={minDate}
                                                maxDate={maxDate}
                                                loading={loading}
                                                shouldDisableDate={shouldDisableDate}
                                                shouldDisableTime={shouldDisableTime}
                                                renderLoading={() => <CalendarPickerSkeleton />}
                                                showTodayButton
                                                todayText={t("date.today")}
                                                open={pickerOpen}
                                                onClose={() => setPickerOpen(false)}
                                            />
                                        </LocalizationProvider>

                                        {isMySite && (
                                            <Box sx={{
                                                position: "absolute",
                                                right: 0,
                                                top: "50%",
                                                transform: "translateY(-50%)",
                                                pointerEvents: "none"
                                            }}>
                                                <IconButton
                                                    sx={{ p: 1, pointerEvents: "all" }}
                                                    onClick={() => toggleBottomDrawer(!report.loading && selectedReport.exists ? "report" : "diary")}
                                                    color="primary"
                                                    disabled={loading}
                                                >
                                                    <Settings sx={{ fontSize: "1.66rem" }} />
                                                </IconButton>
                                            </Box>
                                        )}

                                    </Box>

                                </Grid>

                                {isMySite && (
                                    <Grid
                                        item
                                        xs={1}
                                        zeroMinWidth
                                        sx={{ height: footerOpen ? "inherit" : "unset" }}
                                    >
                                        <Events
                                            events={events}
                                            footerOpen={footerOpen}
                                            setFooterOpen={() => toggleBottomDrawer("archive")}
                                            preview={true}
                                            loading={loading || loadingEvents}
                                            extended={isMySite}
                                        />
                                    </Grid>
                                )}

                            </Grid>
                        )}

                    </Box>

                    {/* content of the drawer below puller component */}

                    {footerOpen && (
                        <Box
                            sx={{
                                maxHeight: `calc(${fullHeight} - ${drawerBleeding}px)`,
                                width: 'auto',
                                overflowX: 'hidden',
                                mt: "4px"
                            }}
                        >
                            {isMySite ? controlsMySite : controlsArchive}
                        </Box>
                    )}

                </SwipeableDrawer>
            </Container>
        )

    // mobile sider landscape
    } else if (isMobile && isLandscape) {
        return (
            <SwipeableDrawer
                key={initialSiderWidth} // avoid hiding bleed by forcing remount
                anchor="right"
                open={isDrawerOpen}
                onClose={() => toggleSideDrawer(false)}
                onOpen={() => toggleSideDrawer(true)}
                swipeAreaWidth={drawerBleeding + 9} // increases the area from which swiping is possible
                disableSwipeToOpen={false}
                hideBackdrop={true} // prevent background from graying out outside the sider
                sx={{ pointerEvents: "none" }} // disable invisible layer that prevents interaction with the content (= background of the drawer)
                ModalProps={{
                    keepMounted: true,
                    closeAfterTransition: true,
                    disableRestoreFocus: true
                }}
                PaperProps={{
                    sx: {
                        width: initialSiderWidth - drawerBleeding,
                        boxSizing: 'border-box',
                        overflow: 'visible',
                        height: `100%`,
                        pointerEvents: "auto" // necessary to remain interaction with the drawer
                    }
                }}
            >
                <Box
                    sx={{
                        backgroundColor: "white",
                        position: 'absolute',
                        height: '100%',
                        top: 0,
                        right: 0,
                        left: -drawerBleeding,
                        visibility: 'visible',
                        overflow: 'auto',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        overflowX: "hidden"
                    }}
                >

                    {/* puller component to pull out the drawer */}

                    <Box
                        sx={{
                            position: 'absolute',
                            backgroundColor: isDrawerOpen ? '#e0e0e0' : '#003366',
                            height: 30,
                            width: 6,
                            top: `calc(50% + 8px)`,
                            left: 8,
                            borderRadius: 3
                        }}
                    />

                    {/* content of the drawer below puller component */}

                    <Box
                        sx={{
                            overflow: 'auto',
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            mt: `${appbarHeight}px`,
                            ml: `${drawerBleeding}px`,
                            height: "100%",
                            maxHeight: fullHeight,
                            width: `calc(100% - ${drawerBleeding}px)`
                        }}
                    >
                        {isMySite ? controlsMySite : controlsArchive}
                    </Box>
                </Box>
            </SwipeableDrawer>
        )

    // desktop sider
    } else {
        return (
            <Drawer
                variant="permanent"
                anchor="right"
                PaperProps={{
                    sx: {
                        width: initialSiderWidth,
                        boxSizing: 'border-box'
                    }
                }}
            >
                <Toolbar /> {/* provides top distance without additional functionality */}

                <Box
                    ref={refSider}
                    sx={{
                        overflow: 'auto',
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        overflowX: "hidden"
                    }}
                >
                    {isMySite ? controlsMySite : controlsArchive}
                </Box>
            </Drawer>
        )
    }
}